import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography, Grid, Card, CardContent, alpha, useTheme } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupsIcon from '@mui/icons-material/Groups';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const AboutSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: 10,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
          theme.palette.secondary.main,
          0.05
        )} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.6rem', md: '2.1rem' },
                color: theme.palette.text.primary,
              }}
            >
              {t('about_section_title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                mb: 3,
                lineHeight: 1.8,
                fontSize: '1.1rem',
              }}
            >
              {t('about_section_content')}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                mt: 4,
                color: theme.palette.primary.main,
              }}
            >
              {t('why_peppol_title')}
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: theme.palette.text.secondary, fontSize: '1.1rem', lineHeight: 2 }}>
              <li>{t('peppol_compliance')}</li>
              <li>{t('peppol_security')}</li>
              <li>{t('peppol_efficiency')}</li>
              <li>{t('peppol_integration')}</li>
              <li>{t('peppol_cost')}</li>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {[
                {
                  icon: <SecurityIcon sx={{ fontSize: 40 }} />,
                  title: t('secure_compliant'),
                  description: t('secure_compliant_desc'),
                  color: theme.palette.success.main,
                },
                {
                  icon: <SpeedIcon sx={{ fontSize: 40 }} />,
                  title: t('lightning_fast'),
                  description: t('lightning_fast_desc'),
                  color: theme.palette.info.main,
                },
                {
                  icon: <GroupsIcon sx={{ fontSize: 40 }} />,
                  title: t('team_collaboration'),
                  description: t('team_collaboration_desc'),
                  color: theme.palette.warning.main,
                },
                {
                  icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
                  title: t('scalable_solution'),
                  description: t('scalable_solution_desc'),
                  color: theme.palette.error.main,
                },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 16px ${alpha(item.color, 0.2)}`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(item.color, 0.1),
                          color: item.color,
                          mb: 2,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AboutSection;
